import {
  Button,
  DetailList,
  formatDate,
  formatPhone,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@majitap/ui/arc/tabs/tabs";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { type Customer, customerActions, customerQuery, paymentTermsQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { ACQUISITION_SOURCE } from "../../labels";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import { type Acquisition, AcquisitionFields, useStaffName } from "../shared/acquisition-fields";
import { InvoicesPanel, PaymentsPanel } from "../shared/finance";
import { ActivityPanel, CreditPanel, OrdersPanel, WalletPanel } from "../shared/panels";
import { QuickbooksStatus } from "../shared/sync-status";
import styles from "./customers.module.css";
import { CUSTOMER_STATUS } from "./list";
import { PlacesPanel } from "./places";

export const CUSTOMER_TABS = [
  "overview",
  "places",
  "orders",
  "invoices",
  "payments",
  "wallet",
  "credit",
  "activity",
] as const;
export type CustomerTab = (typeof CUSTOMER_TABS)[number];

/**
 * One customer (P8-10, `Pmflg` in the design): who they are, their places and tanks, orders,
 * wallet, credit and history. Tabs the role can't use aren't shown.
 */
export function CustomerPage(): ReactNode {
  const { customerId } = useParams({ from: "/console/customers/$customerId" });
  const search = useSearch({ from: "/console/customers/$customerId" });
  const navigate = useNavigate({ from: "/customers/$customerId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const customer = useQuery(customerQuery(customerId));
  const [dialog, setDialog] = useState<"edit" | "status" | null>(null);
  useRecordCrumb(customer.data?.user.name);

  if (customer.status === "pending") return <PageLoading label="Loading the customer" />;
  if (customer.status === "error") {
    const error = errorOf(customer.error);
    if (error.code === "CUSTOMER_NOT_FOUND")
      return <PageEmpty title="No such customer" description="They may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => customer.refetch()} />;
  }
  const c = customer.data;
  const canManage = permissions.includes("customers.manage");
  const tabs: { id: CustomerTab; label: string; shown: boolean }[] = [
    { id: "overview", label: "Overview", shown: true },
    { id: "places", label: "Places and tanks", shown: true },
    { id: "orders", label: "Orders", shown: permissions.includes("orders.read") },
    { id: "wallet", label: "Wallet", shown: permissions.includes("payments.record") },
    { id: "invoices", label: "Invoices", shown: permissions.includes("invoices.issue") },
    { id: "payments", label: "Payments", shown: permissions.includes("payments.record") },
    { id: "credit", label: "Credit", shown: permissions.includes("invoices.issue") },
    { id: "activity", label: "Activity", shown: permissions.includes("staff.manage") },
  ];
  const visible = tabs.filter((t) => t.shown);
  const tab = visible.some((t) => t.id === search.tab) ? (search.tab as CustomerTab) : "overview";

  return (
    <>
      <PageHeader
        title={c.user.name}
        description={[
          formatPhone(c.user.phone_number),
          c.status === "active" ? null : `${CUSTOMER_STATUS[c.status][0]}: can't sign in`,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          canManage ? (
            <>
              <Button variant="secondary" onClick={() => setDialog("status")}>
                {c.status === "active" ? "Deactivate" : "Reactivate"}
              </Button>
              <Button onClick={() => setDialog("edit")}>Edit</Button>
            </>
          ) : undefined
        }
      />
      <Tabs
        value={tab}
        onValueChange={(v) =>
          navigate({ search: { tab: v === "overview" ? undefined : (v as CustomerTab) }, replace: true })
        }
      >
        <TabsList aria-label="About this customer">
          {visible.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          <Overview customer={c} />
          <QuickbooksStatus entityType="customer" entityId={c.id} />
        </TabsContent>
        <TabsContent value="places">
          <PlacesPanel owner={{ customer_id: c.id }} canEdit={canManage} />
        </TabsContent>
        <TabsContent value="orders">
          {tab === "orders" ? <OrdersPanel owner={{ customer_id: c.id }} /> : null}
        </TabsContent>
        <TabsContent value="invoices">
          {tab === "invoices" ? <InvoicesPanel owner={{ customer_id: c.id }} /> : null}
        </TabsContent>
        <TabsContent value="payments">
          {tab === "payments" ? (
            <PaymentsPanel filter={{ customer_id: c.id }} empty="Payments they make, and refunds to them, show here." />
          ) : null}
        </TabsContent>
        <TabsContent value="wallet">
          {tab === "wallet" ? <WalletPanel owner={{ customer_id: c.id }} /> : null}
        </TabsContent>
        <TabsContent value="credit">
          {tab === "credit" ? (
            <CreditPanel owner={{ kind: "customer", id: c.id }} paymentTermId={c.payment_term_id} />
          ) : null}
        </TabsContent>
        <TabsContent value="activity">
          {tab === "activity" ? (
            <ActivityPanel targetType="customer" id={c.id} label="What changed for this customer" />
          ) : null}
        </TabsContent>
      </Tabs>
      {dialog === "edit" ? <EditCustomerDialog customer={c} onClose={() => setDialog(null)} /> : null}
      {dialog === "status" ? <StatusDialog customer={c} onClose={() => setDialog(null)} /> : null}
    </>
  );
}

function Overview({ customer: c }: { customer: Customer }) {
  const format = useCompanyFormat();
  const terms = useQuery(paymentTermsQuery);
  const wonBy = useStaffName(c.acquired_by_staff_id);
  return (
    <div className={styles.facts}>
      <DetailList
        items={[
          { label: "Name", value: c.user.name },
          { label: "Phone", value: formatPhone(c.user.phone_number) },
          { label: "Email", value: c.user.email },
          {
            label: "Status",
            value: <Badge tone={CUSTOMER_STATUS[c.status][1]}>{CUSTOMER_STATUS[c.status][0]}</Badge>,
          },
          {
            label: "Businesses",
            value: c.businesses.length
              ? c.businesses.map((b) => (
                  <div key={b.id}>
                    <RouterLink href={`/businesses/${b.id}`} className={styles.link}>
                      {b.business_name}
                    </RouterLink>{" "}
                    <span className={styles.muted}>({b.role})</span>
                  </div>
                ))
              : null,
          },
          {
            label: "Payment terms",
            value: terms.data?.find((t) => t.id === c.payment_term_id)?.name ?? "The company's default",
          },
          { label: "KRA PIN", value: c.tax_pin },
          { label: "Customer since", value: formatDate(c.created_at, format) },
          { label: "How they were won", value: c.acquisition_source ? ACQUISITION_SOURCE[c.acquisition_source] : null },
          { label: "Won by", value: wonBy },
        ]}
      />
    </div>
  );
}

function EditCustomerDialog({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(customer.user.name);
  const [email, setEmail] = useState(customer.user.email ?? "");
  const [pin, setPin] = useState(customer.tax_pin ?? "");
  const typedPin = pin.trim().toUpperCase();
  const pinValid = !typedPin || /^[A-Z]\d{9}[A-Z]$/.test(typedPin);
  const [won, setWon] = useState<Acquisition>({
    source: customer.acquisition_source,
    staffId: customer.acquired_by_staff_id,
  });
  const mutation = useMutation({
    mutationFn: () =>
      customerActions.update(customer.id, {
        name: name.trim() !== customer.user.name ? name.trim() : undefined,
        email: email.trim() && email.trim() !== customer.user.email ? email.trim() : undefined,
        // a source can be changed but not cleared; the staff member can be (null)
        acquisition_source: won.source && won.source !== customer.acquisition_source ? won.source : undefined,
        acquired_by_staff_id: won.staffId !== customer.acquired_by_staff_id ? won.staffId : undefined,
        // emptied: removed (null)
        tax_pin: typedPin !== (customer.tax_pin ?? "") ? typedPin || null : undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer", customer.id] });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Edit customer"
        description="The phone number can't change here: it's how the customer signs in."
      >
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            description="A new email is saved unconfirmed."
          />
          <Input
            label="KRA PIN (optional)"
            placeholder="A123456789B"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            description="For invoices in their own name: with it, they're sent to KRA eTIMS when issued."
            error={pinValid ? undefined : "A letter, 9 digits and a letter, e.g. A123456789B"}
          />
          <AcquisitionFields
            value={won}
            onChange={setWon}
            staffLabel="Won by"
            staffHelp="Their orders count as this staff member's sales, unless staff place an order themselves."
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim() || !pinValid} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const queryClient = useQueryClient();
  const deactivate = customer.status === "active";
  const mutation = useMutation({
    mutationFn: () => customerActions.setActive(customer.id, !deactivate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer", customer.id] });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={deactivate ? `Deactivate ${customer.user.name}?` : `Reactivate ${customer.user.name}?`}
        description={
          deactivate
            ? "They're signed out of the customer apps and can't sign in again until reactivated. Their orders and history stay."
            : "They can sign in to the customer apps again."
        }
      >
        <div className={styles.form}>
          {mutation.error ? (
            <Alert tone="danger" title="Nothing changed">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={deactivate ? "danger" : "primary"}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {deactivate ? "Deactivate" : "Reactivate"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
